import { TFunction } from 'i18next';
import { MetricDetails } from '../../../../api/metrics/types';
import { DomainPreferences } from '../../../../api/types';
import { UndefinedValue } from '../../../../constants';
import { sortFieldValues } from '../../../../utils';
import { formatMetricValue } from '../../../../utils-display';
import { DataFieldValueFormatter } from '../../../hooks';
import { removeDuplicates } from '../../../utils';
import {
  DataMapValue,
  MultiDatasetChartData,
} from '../../ui-components/data-viz-ui/charts/common/common-chart-types';
import {
  LabelsToBenchmarkDataMap,
  LineChartData,
} from '../../ui-components/data-viz-ui/charts/line-chart/line-chart-types';
import { OverTimeSegmentedVizDataResponseForMetric } from './types';

export const getLineChartDataFromOverTimeSegmentedResponse = (
  response: OverTimeSegmentedVizDataResponseForMetric,
  metricDetails: MetricDetails,
  preferences: DomainPreferences,
  t: TFunction,
  dataFieldValueFormatter: DataFieldValueFormatter
): LineChartData => {
  const { data } = response;
  const labels = data.map((dataForDate) => dataForDate.dateLabel);
  const { segmentBy } = response;
  const datasetKeys = removeDuplicates(
    data.flatMap((dataForDate) =>
      dataForDate.datapoints.map((datapoint) => {
        return { label: datapoint.segmentVal ?? UndefinedValue };
      })
    )
  );
  const sortedDatasetKeys = sortFieldValues(
    datasetKeys,
    segmentBy.dataField,
    preferences
  ).map((l) => dataFieldValueFormatter([l.label], segmentBy.dataField));

  const valueFormatter = (val: number) => {
    return formatMetricValue(val, metricDetails.formatType);
  };
  const datasets = sortedDatasetKeys.map((datasetKey) => ({
    value: datasetKey,
    valueFormatter,
  }));
  const dataMap: Record<string, DataMapValue[]> = Object.fromEntries(
    data.map((dataForDate) => {
      return [
        dataForDate.dateLabel,
        dataForDate.datapoints.map((datapoint) => {
          return {
            datasetKey: dataFieldValueFormatter(
              [datapoint.segmentVal],
              segmentBy.dataField
            ),
            value: datapoint.datapoint.value,
          };
        }),
      ];
    })
  );

  const hasBenchmark = response.data.first()?.datapoints.first()
    ?.datapoint.hasBenchmark;

  if (!hasBenchmark) {
    return {
      labels,
      dataMap,
      datasets,
      hasBenchmarkData: false,
    };
  } else {
    const labelsToBenchmarkDataMap: Record<string, LabelsToBenchmarkDataMap> =
      Object.fromEntries(
        labels.map((label, i) => {
          const firstDataPoint = data[i].datapoints[0].datapoint;
          // Assuming here that benchmarkDateLabel is the same for all datapoints(i.e. segments)
          const benchmarkDateLabel = firstDataPoint.hasBenchmark
            ? firstDataPoint.benchmarkData.benchmarkDateLabel
            : '';
          return [
            label,
            {
              benchmarkDateLabel,
              data: data[i].datapoints.map((datapoint) => ({
                datasetKey: dataFieldValueFormatter(
                  [datapoint.segmentVal],
                  segmentBy.dataField
                ),
                value: datapoint.datapoint.hasBenchmark
                  ? datapoint.datapoint.benchmarkData.value
                  : 0,
              })),
            },
          ];
        })
      );
    return {
      labels,
      dataMap,
      datasets,
      hasBenchmarkData: true,
      benchmarkDatasetLabelFormatter: (label: string) =>
        `${label}(${t('common:commonValues.misc.benchmark')})`,
      labelsToBenchmarkDataMap,
    };
  }
};

export const getBarChartDataFromOverTimeSegmentedResponse = (
  response: OverTimeSegmentedVizDataResponseForMetric,
  metricDetails: MetricDetails,
  preferences: DomainPreferences,
  t: TFunction,
  dataFieldValueFormatter: DataFieldValueFormatter,
  showTotal?: boolean
): MultiDatasetChartData => {
  const { data } = response;
  const labels = data.map((dataForDate) => dataForDate.dateLabel);
  const { segmentBy } = response;
  const datasetKeys = removeDuplicates(
    data.flatMap((dataForDate) =>
      dataForDate.datapoints.map((datapoint) => {
        return { label: datapoint.segmentVal ?? UndefinedValue };
      })
    )
  );
  const sortedDatasetKeys = sortFieldValues(
    datasetKeys,
    segmentBy.dataField,
    preferences
  ).map((l) => dataFieldValueFormatter([l.label], segmentBy.dataField));

  const valueFormatter = (val: number) => {
    return formatMetricValue(val, metricDetails.formatType);
  };
  const datasets = sortedDatasetKeys.map((datasetKey) => ({
    value: datasetKey,
    valueFormatter,
  }));
  const dataMap: Record<string, DataMapValue[]> = Object.fromEntries(
    data.map((dataForDate) => {
      return [
        dataForDate.dateLabel,
        dataForDate.datapoints.map((datapoint) => {
          return {
            datasetKey: dataFieldValueFormatter(
              [datapoint.segmentVal],
              segmentBy.dataField
            ),
            value: datapoint.datapoint.value,
          };
        }),
      ];
    })
  );
  return {
    labels,
    dataMap,
    datasets,
    type: 'multi-dataset',
    isStackable: true,
    showTotal,
  };
};
